/** Canonical sport helpers for customer website. */
import { getVisiblePitches } from './turfVisibility';

export const SPORT_ORDER = ['CRICKET', 'FOOTBALL', 'PICKLEBALL', 'BADMINTON'] as const;
export type SportKey = (typeof SPORT_ORDER)[number];

export type PitchSportFields = {
  sport?: string | null;
  supportedSports?: string[] | null;
  pitchName?: string | null;
  // Pitch area arrives as a number from the API and as a string in view models.
  area?: string | number | null;
  playingFormat?: string | null;
  id?: string;
  basePrice?: number | null;
  hidePitch?: boolean | null;
};

export const normalizeSport = (sport?: string | null): string | null => {
  if (!sport || typeof sport !== 'string') return null;
  const upper = sport.trim().toUpperCase();
  return (SPORT_ORDER as readonly string[]).includes(upper) ? upper : null;
};

export const getPitchSports = (pitch: PitchSportFields | null | undefined): string[] => {
  if (!pitch) return [];
  const primary = normalizeSport(pitch.sport) || 'CRICKET';
  const supported = (pitch.supportedSports || [])
    .map((s) => normalizeSport(s))
    .filter((s): s is string => !!s);
  return sortSports([...new Set([primary, ...supported])]);
};

export const pitchSupportsSport = (
  pitch: PitchSportFields | null | undefined,
  sport?: string | null
): boolean => {
  const target = normalizeSport(sport);
  if (!target) return false;
  return getPitchSports(pitch).includes(target);
};

export const sortSports = (sports: string[]): string[] => {
  const normalized = sports
    .map((s) => normalizeSport(s))
    .filter((s): s is string => !!s);
  const unique = [...new Set(normalized)];
  return unique.sort((a, b) => {
    const ai = SPORT_ORDER.indexOf(a as SportKey);
    const bi = SPORT_ORDER.indexOf(b as SportKey);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

export const deriveVenueSports = (opts: {
  turfSupportedSports?: string[] | null;
  pitches?: PitchSportFields[] | null;
}): string[] => {
  const fromPitches = new Set<string>();
  for (const pitch of getVisiblePitches(opts.pitches)) {
    for (const s of getPitchSports(pitch)) fromPitches.add(s);
  }
  if (fromPitches.size > 0) return sortSports([...fromPitches]);

  return sortSports(opts.turfSupportedSports || []);
};

/** Empty catalogs are legacy and keep pitch-based booking enabled. */
export const isSportEnabledAtTurf = (
  sport?: string | null,
  turfSupportedSports?: string[] | null
): boolean => {
  const selected = normalizeSport(sport);
  if (!selected) return false;
  const enabled = sortSports(turfSupportedSports || []);
  return enabled.length === 0 || enabled.includes(selected);
};

export const formatSportLabel = (sport?: string | null): string => {
  const s = normalizeSport(sport);
  if (!s) return '';
  return s.charAt(0) + s.slice(1).toLowerCase();
};

// Pitch collections come from GraphQL/view models that are untyped, so these
// return the caller's own shape rather than a narrowed helper type.
export const filterPitchesBySport = (
  pitches: any[] | null | undefined,
  sport?: string | null
): any[] => {
  if (!pitches?.length) return [];
  const visiblePitches = getVisiblePitches(pitches);
  const target = normalizeSport(sport);
  if (!target) return visiblePitches;
  return visiblePitches.filter((p) => pitchSupportsSport(p, target));
};

/**
 * Match a pitch from URL pitchname + optional sport.
 * Prefers exact pitchName, then area+sport (incl. shared), then sport-only.
 */
export const matchPitchForBooking = (
  pitches: any[] | undefined | null,
  opts: {
    pitchName?: string | null;
    pitchArea?: string | null;
    selectedSport?: string | null;
  }
): any | undefined => {
  const visiblePitches = getVisiblePitches(pitches);
  if (!visiblePitches.length) return undefined;
  const { pitchName, pitchArea, selectedSport } = opts;
  const sport = normalizeSport(selectedSport);
  const isAreaNumeric = !!pitchArea && /^\d+$/.test(pitchArea);

  if (pitchName) {
    const byName = visiblePitches.find((p) => p.pitchName === pitchName);
    if (byName && (!sport || pitchSupportsSport(byName, sport))) return byName;
  }

  if (isAreaNumeric) {
    const areaNum = Number(pitchArea);
    const byArea = visiblePitches.find((p) => {
      if (Number(p.area) !== areaNum) return false;
      return sport ? pitchSupportsSport(p, sport) : true;
    });
    if (byArea) return byArea;
  }

  if (sport) {
    return visiblePitches.find((p) => pitchSupportsSport(p, sport));
  }

  return undefined;
};
